import CustomButton from "@components/buttons/CustomButton";
import { CurrencyLogoDropdown } from "@components/dropdowns/CurrencyLogoDropdown";
import GlobalTextInputField from "@components/inputs/GlobalTextInputField";
import SlidingPanel from "@components/sliders/SlidingPanel";
import { invoiceServices } from "@services/apis/Invoice";
import { AddOfframpChargeRequest } from "@services/models/InvoiceApprovals/GetOfframpChargesModel";
import { GetSupportedBaseCurrencies } from "@services/models/InvoiceApprovals/GetSupportedBaseCurrencies";
import React, { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import useSnackbarMessage from "../../../utils/HandleSuccessErrorSnackbars";

interface OfframpChargeManagementSliderProps {
  isOpen: boolean;
  onClose: () => void;
  onChargeAdded: () => void;
}

const OfframpChargeManagementSlider: React.FC<
  OfframpChargeManagementSliderProps
> = ({ isOpen, onClose, onChargeAdded }) => {
  const { handleSuccess, handleError } = useSnackbarMessage();

  const [merchantId, setMerchantId] = useState<string>("");
  const [chargePercentage, setChargePercentage] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [supportedOffRampCurrencies, setSupportedOffRampCurrencies] = useState<
    GetSupportedBaseCurrencies[]
  >([]);
  const [offRampCurrenciesLoading, setOffRampCurrenciesLoading] =
    useState<boolean>(true);
  const [selectedOffRampCurrency, setSelectedOffRampCurrency] = useState<
    number | undefined
  >(undefined);

  const handleSubmit = async () => {
    if (!chargePercentage.trim()) {
      handleError({ message: "Please enter charge percentage" });
      return;
    }

    const chargeValue = Number(chargePercentage);
    if (isNaN(chargeValue) || chargeValue < 0 || chargeValue > 100) {
      handleError({ message: "Charge percentage must be between 0 and 100" });
      return;
    }

    if (merchantId && isNaN(Number(merchantId))) {
      handleError({ message: "Merchant ID must be a valid number" });
      return;
    }

    try {
      setIsLoading(true);
      const requestData: AddOfframpChargeRequest = {
        chargePercentage: chargeValue,
        description: description.trim() || undefined,
        isActive: true,
        currencyId: selectedOffRampCurrency,
        currencySymbol:
          supportedOffRampCurrencies.find(
            (currency) => currency.currencyId === selectedOffRampCurrency
          )?.currencySymbol || undefined,
      };

      // Only add merchantId if provided
      if (merchantId.trim()) {
        requestData.merchantId = Number(merchantId);
      }

      await invoiceServices.addOfframpCharge(requestData);

      handleSuccess({ message: "Offramp charge added successfully!" });

      resetForm();

      if (onChargeAdded) {
        onChargeAdded();
      }

      onClose();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.msg || "Failed to add offramp charge";
      handleError({ message: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSupportedOffRampCurrencies = async () => {
    try {
      setOffRampCurrenciesLoading(true);
      const response = await invoiceServices.getSupportedBaseCurrenciesList({
        isOffRampSupported: 1,
      });
      setSupportedOffRampCurrencies(response.data);
      setOffRampCurrenciesLoading(false);
    } catch (error) {
      setOffRampCurrenciesLoading(false);
    }
  };

  const resetForm = () => {
    setMerchantId("");
    setChargePercentage("");
    setDescription("");
  };

  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen]);

  const handleOffRampSettlementCurrencySelection = (value: string | number) => {
    setSelectedOffRampCurrency(Number(value));
  };

  useEffect(() => {
    if (isOpen) {
      fetchSupportedOffRampCurrencies();
    }
  }, [isOpen]);

  const isFormValid = Boolean(chargePercentage.trim());

  return (
    <SlidingPanel
      isOpen={isOpen}
      heading={"Add Offramp Charge"}
      onClose={() => {
        resetForm();
        onClose();
      }}
      onClickCross={() => {
        resetForm();
        onClose();
      }}
      showConfirm={false}
      isSlider={true}
      widthDesktop={"35%"}
    >
      <div className="flex flex-col px-[16px] py-[16px] mt-[60px]">
        <div className="flex flex-col gap-4 bg-bg-base rounded-lg p-4 border border-border-subtle">
          <GlobalTextInputField
            placeholder="Enter merchant ID (leave empty for default charge)"
            value={merchantId}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setMerchantId(e.target.value)
            }
            title="Merchant ID (Optional)"
          />

          <GlobalTextInputField
            placeholder="Enter charge percentage (0-100)"
            value={chargePercentage}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setChargePercentage(e.target.value)
            }
            title="Charge Percentage"
          />

          <GlobalTextInputField
            placeholder="Enter description for this charge"
            value={description}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setDescription(e.target.value)
            }
            title="Description (Optional)"
          />
          {!offRampCurrenciesLoading ? (
            <CurrencyLogoDropdown
              listOfValues={supportedOffRampCurrencies.map(
                (supportedOffRampCurrency) => ({
                  id: supportedOffRampCurrency.currencyId,
                  currency: supportedOffRampCurrency.currencySymbol,
                  currencyLogo: supportedOffRampCurrency.currencyImg,
                  currencyName: supportedOffRampCurrency.currencyName,
                  isToken: true,
                  isReceive: true,
                })
              )}
              enableSearch={true}
              searchableValues={supportedOffRampCurrencies.map((value) => [
                value.currencySymbol,
                value.currencyName,
              ])}
              value={selectedOffRampCurrency ?? undefined}
              onChange={handleOffRampSettlementCurrencySelection}
              label="Select Final Settlement Currency*"
              color="bg-main-container-header"
              baseColor="bg-bg-page-body"
              expandedBorderColor="border-button-primary"
              borderColor="border-subtle"
            />
          ) : (
            <Skeleton height={44} className="mb-1" />
          )}
          <CustomButton
            text="ADD CHARGE"
            onClick={handleSubmit}
            isActive={isFormValid && !isLoading}
            isButtonLoading={isLoading}
            className="flex-1 w-full"
          />
        </div>
      </div>
    </SlidingPanel>
  );
};

export default OfframpChargeManagementSlider;
